'use client';

import { c as _c } from "react/compiler-runtime";
import classnames from 'classnames';
import { type KeyboardEvent, type ReactElement, useRef, useState } from 'react';
import { useFocusWithin } from 'react-aria';
import { Menu, MenuItem, type MenuItemProps, type MenuProps, MenuTrigger, Popover, type PopoverProps } from 'react-aria-components';
import { KeyboardKey } from '@shared/utils/constants/keyboard-keys';
import { useClickOutside } from '@shared/utils/hooks/use-click-outside';
import { MoreIcon } from '../svgs';
import styles from './dropdown-menu.module.scss';
export type DropdownMenuProps<T = object> = {
  className?: string;
  closeOnClickOutside?: boolean;
  closeOnEscapeKeyUp?: boolean;
  closeOnMenuBlur?: boolean;
  closeOnMenuItemClick?: boolean;
  containerPadding?: number;
  items: DropdownMenuItem[];
  menuClassName?: string;
  menuItemClassName?: string;
  menuPlacement?: PopoverProps['placement'];
  menuProps?: MenuProps<T>;
  selected?: boolean;
  shouldMenuFlip?: boolean;
  triggerClassName?: string;
  triggerDataTestId?: string;
  TriggerIcon?: ReactElement;
  triggerLabel?: string;
};
type DropdownMenuItem = MenuItemProps & {
  Icon?: ReactElement;
  selected?: boolean;
  withSeparatorAfter?: boolean;
  withSeparatorBefore?: boolean;
};
export const DropdownMenu = t0 => {
  const $ = _c(53);
  const {
    className,
    closeOnClickOutside: t1,
    closeOnEscapeKeyUp: t2,
    closeOnMenuBlur: t3,
    closeOnMenuItemClick: t4,
    containerPadding,
    items,
    menuClassName,
    menuItemClassName,
    menuPlacement,
    menuProps,
    selected,
    shouldMenuFlip: t5,
    triggerClassName,
    triggerDataTestId,
    TriggerIcon,
    triggerLabel
  } = t0;
  const closeOnClickOutside = t1 === undefined ? true : t1;
  const closeOnEscapeKeyUp = t2 === undefined ? true : t2;
  const closeOnMenuBlur = t3 === undefined ? true : t3;
  const closeOnMenuItemClick = t4 === undefined ? true : t4;
  const shouldMenuFlip = t5 === undefined ? false : t5;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  let t6;
  if ($[0] !== closeOnClickOutside) {
    t6 = () => {
      if (closeOnClickOutside) {
        setIsOpen(false);
      }
    };
    $[0] = closeOnClickOutside;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const wrapperRef = useClickOutside(t6);
  let t7;
  if ($[2] !== closeOnMenuBlur) {
    t7 = {
      onBlurWithin: event => closeOnMenuBlur && event.relatedTarget !== triggerRef.current && setIsOpen(false)
    };
    $[2] = closeOnMenuBlur;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const {
    focusWithinProps
  } = useFocusWithin(t7);
  let t8;
  if ($[4] !== closeOnMenuItemClick) {
    t8 = () => {
      if (closeOnMenuItemClick) {
        setTimeout(() => setIsOpen(false));
      }
    };
    $[4] = closeOnMenuItemClick;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const handleMenuClick = t8;
  let t9;
  if ($[6] !== closeOnEscapeKeyUp) {
    t9 = event_0 => {
      if (closeOnEscapeKeyUp && event_0.key === KeyboardKey.Escape) {
        setIsOpen(false);
      }
    };
    $[6] = closeOnEscapeKeyUp;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const handleWrapperKeyUp = t9;
  let t10;
  if ($[8] !== className) {
    t10 = classnames(styles.wrapper, className);
    $[8] = className;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== selected || $[11] !== triggerClassName) {
    t11 = classnames(styles.trigger, triggerClassName, {
      [styles["trigger--selected"]]: selected
    });
    $[10] = selected;
    $[11] = triggerClassName;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== isOpen) {
    t12 = () => setIsOpen(!isOpen);
    $[13] = isOpen;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== TriggerIcon) {
    t13 = TriggerIcon ?? <div className={styles["trigger__more-icon-wrapper"]}><MoreIcon className={styles["trigger__more-icon"]} /></div>;
    $[15] = TriggerIcon;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== isOpen || $[18] !== t11 || $[19] !== t12 || $[20] !== t13 || $[21] !== triggerDataTestId || $[22] !== triggerLabel) {
    t14 = <button aria-expanded={isOpen} aria-label={triggerLabel} className={t11} data-testid={triggerDataTestId} onClick={t12} ref={triggerRef} tabIndex={0}>{t13}</button>;
    $[17] = isOpen;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = triggerDataTestId;
    $[22] = triggerLabel;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = containerPadding || 24;
  let t16;
  if ($[24] !== menuClassName) {
    t16 = classnames(styles.menu, menuClassName);
    $[24] = menuClassName;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== items || $[27] !== menuItemClassName) {
    let t18;
    if ($[29] !== menuItemClassName) {
      t18 = t19 => {
        const {
          children,
          Icon,
          id,
          selected: itemSelected,
          withSeparatorAfter,
          withSeparatorBefore,
          ...rest
        } = t19;
        return <MenuItem className={classnames(styles["menu-item"], {
          [styles["menu-item--selected"]]: itemSelected,
          [styles["menu-item--with-separator-after"]]: withSeparatorAfter,
          [styles["menu-item--with-separator-before"]]: withSeparatorBefore
        }, menuItemClassName)} id={id} key={id} {...rest}><>{Icon && <div className={styles["menu-item-icon"]}>{Icon}</div>}{children}</></MenuItem>;
      };
      $[29] = menuItemClassName;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    t17 = items.map(t18);
    $[26] = items;
    $[27] = menuItemClassName;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[31] !== handleMenuClick || $[32] !== menuProps || $[33] !== t16 || $[34] !== t17) {
    t18 = <Menu className={t16} onAction={handleMenuClick} {...menuProps}>{t17}</Menu>;
    $[31] = handleMenuClick;
    $[32] = menuProps;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== focusWithinProps || $[37] !== t18) {
    t19 = <div {...focusWithinProps}>{t18}</div>;
    $[36] = focusWithinProps;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== menuPlacement || $[40] !== shouldMenuFlip || $[41] !== t15 || $[42] !== t19) {
    t20 = <Popover className={styles.popover} containerPadding={t15} isNonModal={true} placement={menuPlacement} shouldFlip={shouldMenuFlip} triggerRef={triggerRef}>{t19}</Popover>;
    $[39] = menuPlacement;
    $[40] = shouldMenuFlip;
    $[41] = t15;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== isOpen || $[45] !== t14 || $[46] !== t20) {
    t21 = <MenuTrigger isOpen={isOpen}>{t14}{t20}</MenuTrigger>;
    $[44] = isOpen;
    $[45] = t14;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== handleWrapperKeyUp || $[49] !== t10 || $[50] !== t21 || $[51] !== wrapperRef) {
    t22 = <div className={t10} onKeyUp={handleWrapperKeyUp} ref={wrapperRef}>{t21}</div>;
    $[48] = handleWrapperKeyUp;
    $[49] = t10;
    $[50] = t21;
    $[51] = wrapperRef;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  return t22;
};